import { useEffect, useState } from 'react';
import { fetchRoles, Role } from '../services/userService';

export interface RolesLookup {
  roles: Role[];
  loading: boolean;
  refresh: () => void;
}

let cachedRoles: Role[] | null = null;
let inFlight: Promise<Role[]> | null = null;
const subscribers = new Set<(roles: Role[]) => void>();

const load = (force?: boolean): Promise<Role[]> => {
  if (force) { cachedRoles = null; inFlight = null; }
  if (cachedRoles) return Promise.resolve(cachedRoles);
  if (!inFlight) {
    inFlight = fetchRoles().then((roles) => {
      cachedRoles = roles;
      inFlight = null;
      subscribers.forEach((cb) => cb(roles));
      return roles;
    });
  }
  return inFlight;
};

/** Fetches the live role catalog (`app.user_role`) once per session (module-level cache,
 * deduped in-flight fetch) so every component that needs role names/labels/hierarchy —
 * UserList, InviteUserDialog, Navbar, Customer Health — shares one GraphQL call. */
export const useRoles = (): RolesLookup => {
  const [roles, setRoles] = useState<Role[]>(cachedRoles || []);
  const [loading, setLoading] = useState(!cachedRoles);

  useEffect(() => {
    subscribers.add(setRoles);
    if (!cachedRoles) {
      setLoading(true);
      load().then((r) => { setRoles(r); setLoading(false); });
    }
    return () => { subscribers.delete(setRoles); };
  }, []);

  const refresh = () => { setLoading(true); load(true).then((r) => { setRoles(r); setLoading(false); }); };

  return { roles, loading, refresh };
};
